import { IS_IO } from 'lib/site';

/** Once the offer has been on screen, leave the learner alone for a day. */
export const OFFER_COOLDOWN_MS = 24 * 60 * 60 * 1000;

/** What is known about the learner's own subscription: loaded, free, paying, or not yet answered. */
export type SubscriptionState = 'unknown' | 'none' | 'active';

export interface OfferContext {
  isPortal: boolean;
  isGuest: boolean;
  isPreview: boolean;
  subscription: SubscriptionState;
  /** The organisation tier on the profile; a paid one means someone else already pays. */
  organizationTier: string | undefined;
  hasOffer: boolean;
  lastOfferAt: number | null;
  now: number;
}

const FREE_TIERS = new Set(['FREE', 'EXPIRED']);

/** Whether the plan offer belongs in this learner's end-of-test sequence. Every doubt means no. */
export function canOfferPlan(context: OfferContext): boolean {
  // Learner plans are sold on examing.uz only.
  if (IS_IO) return false;
  if (!context.isPortal || context.isGuest || context.isPreview || !context.hasOffer) return false;
  if (context.subscription !== 'none') return false;
  if (context.organizationTier && !FREE_TIERS.has(context.organizationTier)) return false;

  const last = context.lastOfferAt;
  const validLast = typeof last === 'number' && Number.isFinite(last) && last <= context.now;
  return !validLast || context.now - last >= OFFER_COOLDOWN_MS;
}

const keyFor = (userId: string): string => `examing:session-end-offer:${userId}`;

/** Per-learner memory of the last time the offer was shown. Storage can be blocked; then it simply forgets. */
export const offerCooldown = {
  read(userId: string): number | null {
    try {
      const raw = window.localStorage.getItem(keyFor(userId));
      const value = raw === null ? Number.NaN : Number(raw);
      return Number.isFinite(value) ? value : null;
    } catch {
      return null;
    }
  },
  mark(userId: string, at: number): void {
    try {
      window.localStorage.setItem(keyFor(userId), String(at));
    } catch {
      // Without storage the offer may come back next test; that is acceptable.
    }
  },
};
